import { useMemo } from "react";

/**
 * Identity helper. Configs are plain objects; wrapping them keeps every
 * declaration greppable and gives editors a single place to hang JSDoc.
 *
 * @typedef {Object} InlineFieldSpec
 * @property {string}   [label]        Arabic label, used for the pencil's aria-label.
 * @property {string|function} [type]  One of INLINE_EDITORS' keys, or (row) => key.
 * @property {boolean}  [required]
 * @property {function} [getValue]     (source, row) => value seeding the editor.
 * @property {function} [toPatch]      (value, source, row) => partial payload.
 * @property {string}   [payloadKey]   Shorthand when only the key name differs.
 * @property {boolean}  [hydrateOnOpen] GET the record when the pencil is tapped.
 * @property {string}   [permission]   Overrides the config-level permission.
 * @property {function} [validate]     (value, row) => Arabic error | null.
 *
 * @typedef {Object} InlineEditConfig
 * @property {string}   api            factory.get() key — see src/api/apiFactory.js.
 * @property {function} toPayload      (source, row) => the entity's full PUT body.
 * @property {Object.<string, InlineFieldSpec>} fields  Keyed by COLUMN field name.
 * @property {string}   [permission]
 * @property {boolean}  [hydrate]      GET the record before PUT. Default true.
 * @property {function} [canEdit]      (row) => boolean row-level gate.
 * @property {string}   [idField]      Default "id".
 * @property {function} [getId]        (row) => id, wins over idField.
 * @property {function} [pickDetail]   (res) => the record inside the response.
 * @property {string}   [successMessage]
 */
export const defineInlineEdit = (config) => config;

/**
 * Binds a static entity config to the page's refresh closure.
 *
 * `onSaved` is normally `() => dispatch(Entity.getAll(_SearchBody))` — inline
 * saves bypass the entity's update thunk (see useInlineSave for why), so the
 * list refresh has to come from the caller.
 */
export const useInlineEditConfig = (config, onSaved, deps = []) =>
  useMemo(
    () => (config ? { ...config, onSaved } : undefined),
    // `onSaved` is a new closure on every render, so it can't be a dependency —
    // `deps` is what actually decides when it has gone stale (normally the
    // page's search body or its local pagination state).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, ...deps],
  );
